import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { categorySchema } from "../../../lib/schemas/categorySchema";
import { useCategories } from "../../../lib/hooks/useCategories";
import { updateCategory } from "../../../lib/services/categoryService";

/* Centered modal with backdrop */
const UpdateCategoryForm = ({ onClose }) => {
  const { categories, setCategories } = useCategories();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(categorySchema),
    mode: "onChange",
    defaultValues: {
      id: undefined,
      name: undefined,
    },
  });

  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [isSelecting, setIsSelecting] = useState(true);

  useEffect(() => {
    setValue("id", selectedCategory.id);
    setValue("name", selectedCategory.name);
  }, [selectedCategory, setValue]);

  // Close with ESC key
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSubmit = async () => {
    const id = getValues("id");
    const response = await updateCategory(id, getValues("name"));
    const data = await response.json();

    if (response.ok) {
      const oldStencils = categories.filter(
        (cat) => Number(cat.id) !== Number(id)
      );
      const unsorted = [
        ...oldStencils,
        { ...data.data, id: Number(data.data.id) },
      ];
      const sorted = unsorted.sort(function (a, b) {
        return a.id - b.id;
      });
      setCategories(sorted);
    } else {
      alert(`Error updating category: ${data.error}`);
    }

    onClose();
  };

  return (
    <div
      className="confirm-overlay rounded mt-5"
      role="presentation"
      onClick={onClose}
    >
      {/* stop propagation so clicks inside the card don't close */}
      <div
        className="confirm-card col-2"
        role="dialog"
        aria-modal="true"
        style={{
          width: "auto",
          backgroundColor: "rgb(237, 71, 9)",
          outline: "none",
          border: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <>
          {!!isSelecting ? (
            <div className="d-flex flex-column gap-4" style={{ width: "40vh" }}>
              <div className="form-group flex-row" style={{ color: "white" }}>
                <label className="form-label">Select Category to Update</label>
                <select
                  value={selectedCategory.id}
                  onChange={(e) =>
                    setSelectedCategory(
                      categories.find(
                        (cat) => cat.id === Number(e.target.value)
                      )
                    )
                  }
                  size={6}
                >
                  {categories.map((category) => {
                    return (
                      <option
                        value={category.id}
                        key={`category-${category.id}`}
                      >
                        {category.id} - {category.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="confirm-actions">
                <button
                  className="btn confirm-no"
                  style={{
                    outline: "none",
                    border: "none",
                    color: "white",
                    backgroundColor: "darkgreen",
                  }}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn confirm-yes"
                  style={{
                    outline: "none",
                    border: "none",
                    color: "white",
                    backgroundColor: "darkgreen",
                  }}
                  onClick={() => setIsSelecting(false)}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="d-flex flex-row gap-5" style={{ color: "white" }}>
                <div
                  className="d-flex flex-column gap-4"
                  style={{ width: "40vh" }}
                >
                  <div className="form-group flex-row">
                    <div>
                      <label className="form-label">Category Name</label>
                      <input type="text" {...register("name")} />
                    </div>
                    <div>
                      {errors.name && <small>{errors.name.message}</small>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="confirm-actions mt-4">
                <button
                  className="btn confirm-no"
                  style={{
                    outline: "none",
                    border: "none",
                    color: "white",
                    backgroundColor: "darkgreen",
                  }}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn confirm-yes"
                  style={{
                    outline: "none",
                    border: "none",
                    color: "white",
                    backgroundColor: "darkgreen",
                  }}
                  disabled={errors?.name}
                >
                  Update Category
                </button>
              </div>
            </form>
          )}
        </>
      </div>
    </div>
  );
};

export default UpdateCategoryForm;
